export type LayoutBox = { id: string; left: number; top: number; width: number; height: number };
export type LayoutMovement = { id: string; dx: number; dy: number };
export type Alignment = "left" | "center-x" | "right" | "top" | "center-y" | "bottom";

function bounds(boxes: LayoutBox[]) {
  const left = Math.min(...boxes.map((box) => box.left));
  const top = Math.min(...boxes.map((box) => box.top));
  const right = Math.max(...boxes.map((box) => box.left + box.width));
  const bottom = Math.max(...boxes.map((box) => box.top + box.height));
  return { left, top, right, bottom, width: right - left, height: bottom - top };
}
export function alignSelectionToPage(boxes: LayoutBox[], page: { width: number; height: number }, alignment: Alignment): LayoutMovement[] {
  if (!boxes.length) return [];
  const selection = bounds(boxes);
  let dx = 0; let dy = 0;
  if (alignment === "left") dx = -selection.left;
  if (alignment === "center-x") dx = (page.width - selection.width) / 2 - selection.left;
  if (alignment === "right") dx = page.width - selection.right;
  if (alignment === "top") dy = -selection.top;
  if (alignment === "center-y") dy = (page.height - selection.height) / 2 - selection.top;
  if (alignment === "bottom") dy = page.height - selection.bottom;
  return boxes.map((box) => ({ id: box.id, dx, dy }));
}

export function alignObjects(boxes: LayoutBox[], alignment: Alignment): LayoutMovement[] {
  if (boxes.length < 2) return [];
  const selection = bounds(boxes);
  return boxes.map((box) => {
    let dx = 0; let dy = 0;
    if (alignment === "left") dx = selection.left - box.left;
    if (alignment === "center-x") dx = selection.left + selection.width / 2 - (box.left + box.width / 2);
    if (alignment === "right") dx = selection.right - (box.left + box.width);
    if (alignment === "top") dy = selection.top - box.top;
    if (alignment === "center-y") dy = selection.top + selection.height / 2 - (box.top + box.height / 2);
    if (alignment === "bottom") dy = selection.bottom - (box.top + box.height);
    return { id: box.id, dx, dy };
  });
}

export function distributeObjects(boxes: LayoutBox[], axis: "horizontal" | "vertical"): LayoutMovement[] {
  if (boxes.length < 3) return [];
  const ordered = [...boxes].sort((a, b) => axis === "horizontal" ? a.left - b.left : a.top - b.top);
  const start = axis === "horizontal" ? ordered[0]!.left : ordered[0]!.top;
  const last = ordered.at(-1)!;
  const end = axis === "horizontal" ? last.left + last.width : last.top + last.height;
  const occupied = ordered.reduce((total, box) => total + (axis === "horizontal" ? box.width : box.height), 0);
  const gap = (end - start - occupied) / (ordered.length - 1);
  let cursor = start;
  return ordered.map((box) => {
    const current = axis === "horizontal" ? box.left : box.top;
    const delta = cursor - current;
    cursor += (axis === "horizontal" ? box.width : box.height) + gap;
    return { id: box.id, dx: axis === "horizontal" ? delta : 0, dy: axis === "vertical" ? delta : 0 };
  });
}
